import React, { useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getInvoicePartyName,
  getInvoiceShoppingWala,
  isGuestInvoice,
} from "../../../utils/invoice";
import { fmtReportRs } from "../../../utils/reportSummary";
import { formatDateTime } from "../../../utils/dateTime";
import EditCustomerPageModal from "../../../components/Modals/EditCustomerPageModal";
import RowActionsMenu from "../../../components/RowActionsMenu/RowActionsMenu";
import { updateCustomerPageNoInReport } from "../../../store/Slices/ReportSlice";

const getAppBasePath = () =>
  Number(localStorage.getItem("role")) === 1 ? "/admin" : "/branch";

const formatInvoiceDate = (invoice) => {
  const unix = Number(invoice?.date);
  if (Number.isFinite(unix) && unix > 0) return formatDateTime(unix);
  if (invoice?.f_date) return formatDateTime(invoice.f_date);
  return "—";
};

const formatInvoiceNo = (invoice) => {
  const manual = invoice?.invoice_no;
  const auto =
    invoice?.auto_invoice_no != null ? `#${invoice.auto_invoice_no}` : "";
  if (manual && auto) return `${manual} · Auto ${auto}`;
  return manual || auto || "—";
};

const resolvePageNo = (invoice) => {
  const page = invoice?.customerId?.page_no ?? invoice?.page_no;
  const text = page != null ? String(page).trim() : "";
  return text || "";
};

const getShoppingTotal = (items = []) =>
  items
    .filter((item) => String(item?.unit || "").trim().toUpperCase() === "SH")
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

const resolveBranchLabel = (invoice) => {
  const branch = invoice?.branchId;
  if (!branch) return "";
  if (typeof branch === "object") {
    return branch.name || branch.branch_name || "";
  }
  return "";
};

const VARIANT_STYLES = {
  sale: {
    header: "bg-[#155f60]/5 border-[#155f60]/10",
    badge: "bg-[#155f60]/10 text-[#155f60] border-[#155f60]/20",
    label: "Sale",
    amount: "text-[#155f60]",
  },
  guest: {
    header: "bg-warningSoft border-warning/20",
    badge: "bg-warningSoft text-warning border-warning/20",
    label: "Guest Sale",
    amount: "text-warning",
  },
  return: {
    header: "bg-warningSoft border-warning/20",
    badge: "bg-warningSoft text-warning border-warning/20",
    label: "Return",
    amount: "text-warning",
  },
  "guest-return": {
    header: "bg-dangerSoft border-danger/20",
    badge: "bg-dangerSoft text-danger border-danger/20",
    label: "Guest Return",
    amount: "text-danger",
  },
};

const ReportInvoiceCard = ({
  invoice,
  variant = "sale",
  editable = false,
  onPageUpdated,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [pageModalOpen, setPageModalOpen] = useState(false);
  const [pageNoOverride, setPageNoOverride] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const items = invoice?.items || [];
  const shoppingTotal = getShoppingTotal(items);
  const branchLabel = resolveBranchLabel(invoice);
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.sale;
  const pageNo =
    pageNoOverride != null ? String(pageNoOverride).trim() : resolvePageNo(invoice);
  const shoppingWala = getInvoiceShoppingWala(invoice);
  const customer =
    invoice?.customerId && typeof invoice.customerId === "object"
      ? invoice.customerId
      : null;
  // Plain clone so modal never touches frozen Redux customer
  const customerForModal = customer ? { ...customer } : null;
  const canEditPage = Boolean(customer?._id) && !isGuestInvoice(invoice);
  const showActions = Boolean(invoice?._id);

  const goToPrint = () => {
    if (!invoice?._id) return;
    const cid =
      customer?._id ||
      (typeof invoice.customerId === "string" ? invoice.customerId : "") ||
      "guest";
    navigate(`${getAppBasePath()}/invoice/${invoice._id}/${cid}`);
  };

  const goToEdit = () => {
    if (!invoice?._id) return;
    navigate(`${getAppBasePath()}/edit-customers-invoice/${invoice._id}`);
  };

  return (
    <article className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
      <div className={`px-4 py-3 border-b ${styles.header}`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span
            className={`text-xs font-bold px-2.5 py-1 rounded-full border ${styles.badge}`}
          >
            {styles.label}
          </span>
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold text-muted tabular-nums">
              {formatInvoiceDate(invoice)}
            </span>
            {showActions && (
              <RowActionsMenu
                row={invoice}
                onPrint={goToPrint}
                onEdit={editable ? goToEdit : undefined}
                onEditPage={
                  canEditPage ? () => setPageModalOpen(true) : undefined
                }
              />
            )}
          </div>
        </div>

        <h3 className="mt-2 text-base font-bold text-ink leading-snug break-words">
          {getInvoicePartyName(invoice) || "—"}
          {shoppingWala ? ` / ${shoppingWala}` : ""}
        </h3>

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
          <span className="font-semibold text-inkSecondary">
            Inv {formatInvoiceNo(invoice)}
            {pageNo ? ` · Pg ${pageNo}` : ""}
          </span>
          {branchLabel && (
            <span className="rounded-md bg-subtle px-2 py-0.5 font-medium text-inkSecondary">
              {branchLabel}
            </span>
          )}
        </div>

        {invoice?.desc && (
          <p className="mt-2 text-xs text-muted line-clamp-2">{invoice.desc}</p>
        )}
      </div>

      <div className="px-4 py-2.5 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted font-semibold leading-none mb-0.5">
            Total
          </p>
          <p className={`font-extrabold tabular-nums ${styles.amount}`}>
            {fmtReportRs(invoice?.total_amount)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted font-semibold leading-none mb-0.5">
            Shopping
          </p>
          <p className="font-bold text-ink tabular-nums">
            {fmtReportRs(shoppingTotal)}
          </p>
        </div>
        {Number(invoice?.discount) > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted font-semibold leading-none mb-0.5">
              Discount
            </p>
            <p className="font-semibold text-warning tabular-nums">
              {fmtReportRs(invoice.discount)}
            </p>
          </div>
        )}
        {Number(invoice?.payment) > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted font-semibold leading-none mb-0.5">
              Paid
            </p>
            <p className="font-semibold text-success tabular-nums">
              {fmtReportRs(invoice.payment)}
            </p>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="border-t border-line">
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm font-semibold text-inkSecondary hover:bg-subtle transition-colors"
          >
            <span>
              {items.length} item{items.length !== 1 ? "s" : ""}
            </span>
            {expanded ? <FiChevronUp /> : <FiChevronDown />}
          </button>

          {expanded && (
            <div className="px-4 pb-3 space-y-2">
              {items.map((item, index) => (
                <div
                  key={item._id || index}
                  className="rounded-lg border border-line bg-subtle px-3 py-2"
                >
                  <p className="text-sm font-semibold text-ink break-words">
                    {item.name || "—"}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted">
                    <span>
                      Qty: {Number(item.qty || 0).toLocaleString()}{" "}
                      {item.unit || ""}
                    </span>
                    <span>Rate: {fmtReportRs(item.price)}</span>
                    <span className="font-semibold text-inkSecondary">
                      {fmtReportRs(item.amount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {canEditPage && (
        <EditCustomerPageModal
          Open={pageModalOpen}
          setOpen={setPageModalOpen}
          customer={customerForModal}
          onSuccess={(nextPage) => {
            setPageNoOverride(nextPage);
            if (customer?._id) {
              dispatch(
                updateCustomerPageNoInReport({
                  customerId: customer._id,
                  page_no: nextPage,
                })
              );
            }
            if (typeof onPageUpdated === "function") {
              onPageUpdated(invoice, nextPage);
            }
          }}
        />
      )}
    </article>
  );
};

export default ReportInvoiceCard;
